import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useAppAbility } from '@/hooks/useAppAbility';
import { useAutoSave } from '@/hooks/useAutoSave';
import { DataTransformer } from '@/services/DataTransformer';
import { QueryKeys } from '@/services/QueryKeys';
import { Action, Location, LocationProfilesService, UpdateLocationProfileDto } from '@/services/api';
import { useGlobalStore } from '@/stores/useGlobalStore';

export const useLocationProfileForm = () => {
  const { locationId } = useParams();

  const ability = useAppAbility();

  const methods = useForm<UpdateLocationProfileDto>();
  const { setLastSaved } = useGlobalStore((state) => state.actions);

  useEffect(() => () => setLastSaved(undefined), [setLastSaved]);

  const queryClient = useQueryClient();
  const { data: locationProfile } = useQuery({
    queryKey: QueryKeys.locationProfiles.id(locationId as string),
    queryFn: () => LocationProfilesService.findOne({ locationId: locationId as string }),
    enabled: !!locationId,
  });
  const { mutate } = useMutation({
    mutationFn: (formData: UpdateLocationProfileDto) =>
      LocationProfilesService.update({ locationId: locationId as string, requestBody: formData }),

    onMutate: () => setLastSaved(undefined),

    onSuccess: () => {
      setLastSaved(dayjs());

      return queryClient.invalidateQueries({ queryKey: QueryKeys.locationProfiles.id(locationId as string) });
    },
  });

  const handleSubmit = methods.handleSubmit(
    (data) => {
      mutate(DataTransformer.toApi(data, ability.can(Action.UPDATE, locationProfile?.location as Location, 'profile')));
    },
    () => setLastSaved(undefined),
  );
  useAutoSave(handleSubmit, methods.watch);

  const isLoading = !locationProfile;

  const disabled = !locationProfile || ability.cannot(Action.UPDATE, locationProfile?.location as Location, 'profile');

  return { locationProfile, isLoading, disabled, handleSubmit, methods };
};
